import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { SeriesGroup } from '../../../chart.typings'
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state'
import { Box, Checkbox, FormControlLabel, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../../store'
import { ContainsWatchlistItemRequest, UserWatchlists, Watchlist, Watchlist_ShareType, Watchlist_Type, Watchlist_WatchlistItem_Type } from '../../../../../proto/generated/watchlist'
import { ChartContext } from '../../../chart.context'
import { getExchangeAndCodeFromKey, isValidExchange } from '../../../../../util/security'
import { getExpressionFromSaved } from '../../../../../util/expression'
import ExpressionNameDialog from '../../../../watchlist/expressionNameDialog'
import commonActions from '../../../../../actions/common.actions'
import { fetchWatchlists, isSecurityAddedToWatchlists } from '../../../../../services/apis/watchlist'
import { Security, SecurityExpression } from '../../../../../proto/generated/security'
import { selectUserSecurityExpressions } from '../../../../../selectors/security'
import { v4 as uuid } from 'uuid'
import { saveSecurityExpression } from '../../../../../services/apis/expression'
import { addOrUpdateUserExpression } from '../../../../../reducer/security.reducer'
import { selectCommonStore, selectMultiPaneFullScreen } from '../../../../../selectors/common'
import { WidgetListItems } from '../../../../../util/sidebar'
import { MULTIPANE_FULLSCREEN_MODE_ID } from '../../../../../assets/constants'
import { setAddRemoveWatchlistItem } from '../../../../../reducer/watchList.reducer'
import ThinArrowRightIcon from '../../../../../assets/icons/ThinArrowRightIcon'
import AddToWatchlistIcon from '../../../../../assets/icons/AddToWatchlistIcon'
import { useUser } from '../../../../../context/UserContext'
import { setFeatureRestrictionDialog } from '../../../../../reducer/user.reducer'
import { FeatureCategory, FeatureName } from '../../../../adminUserRestrictions/Restrictions.constants'
import { FeatureCode } from '../../../../../util/RestrictionFeatureCode'

export type AddToWatchlistProps = {
    seriesGroup: SeriesGroup
    onClosed: Function
}

const WatchlistMenuItem = (props: { seriesGroup: SeriesGroup; watchlist: Watchlist; addedToWatchlist: boolean; onUpdateWatchlistInState: (id: string, isAdded: boolean) => void }) => {
    const chartContext = useContext(ChartContext)
    const { seriesGroup, watchlist, addedToWatchlist, onUpdateWatchlistInState } = props
    const expressionMode = chartContext?.chart?.mode === 'EXPRESSION'
    const convertedExpression = expressionMode ? chartContext?.chart?.expression : ''
    const [openExpressionNameDialog, setOpenExpressionNameDialog] = useState<boolean>(false)
    const dispatch = useDispatch()
    const savedExpressions: SecurityExpression[] | undefined = useSelector(selectUserSecurityExpressions)
    const { watchlistId: selectedWatchlistId, rightSideWidget } = useSelector(selectCommonStore)
    const { user : {hasAccess}} = useUser()

    const onExpressionNameDialogClosed = useCallback(
        (name: string | null) => {
            if (name !== null) {
                const securities = seriesGroup.securityList.map((securityKey) => {
                    const exchangeAndCode = getExchangeAndCodeFromKey(securityKey)
                    return {
                        exchange: exchangeAndCode[0],
                        code: exchangeAndCode[1]
                    }
                })

                saveSecurityExpression({
                    expression: {
                        id: uuid(),
                        expression: convertedExpression,
                        name: name,
                        securities: securities as Security[],
                        expired: false
                    }
                }).then((response) => {
                    dispatch(addOrUpdateUserExpression(response))
                    commonActions.deleteRecentExpression(dispatch, response.expression)
                    dispatch(setAddRemoveWatchlistItem({
                        watchlistItem: {
                            securityExpression: response,
                            security: undefined,
                            type: Watchlist_WatchlistItem_Type.EXPRESSION
                        },
                        selected: true,
                        watchlistId: watchlist.id,
                        showHighlighted: false
                    }))
                    onUpdateWatchlistInState(watchlist.id, true)
                })
                if (rightSideWidget !== WidgetListItems.WATCHLIST) {
                    commonActions.dispatchRightSideWidget(dispatch, WidgetListItems.WATCHLIST)
                }
                if (selectedWatchlistId !== watchlist.id) {
                    commonActions.dispatchWatchlistId(dispatch, watchlist.id)
                }
            }
            setOpenExpressionNameDialog(false)
        },
        [seriesGroup, convertedExpression, selectedWatchlistId, rightSideWidget, dispatch, watchlist]
    )

    const onAddOrRemovalFromWatchlist = useCallback((addedToWatchlist: boolean) => {
        const savedExpression = getExpressionFromSaved(savedExpressions, convertedExpression)
        if(expressionMode && !hasAccess(FeatureCategory.WATCHLIST, '', FeatureName.COMMUNITY_WATCHLIST_ACCESS)){
             dispatch(setFeatureRestrictionDialog({
                open: true,
                feature: "Security expressions",
                featureRestrictionCode:FeatureCode.SECURITY_EXPRESSIONS
            }))
            return
        }
        if (addedToWatchlist || !expressionMode || (savedExpression && savedExpression!.name)) {
            const exchangeAndCode = getExchangeAndCodeFromKey(seriesGroup.securityList[0])
            if (!isValidExchange(exchangeAndCode[0]) || !exchangeAndCode[1]) {
                return
            }
            if (rightSideWidget !== WidgetListItems.WATCHLIST) {
                commonActions.dispatchRightSideWidget(dispatch, WidgetListItems.WATCHLIST)
            }
            if (selectedWatchlistId !== watchlist.id) {
                commonActions.dispatchWatchlistId(dispatch, watchlist.id)
            }
            dispatch(setAddRemoveWatchlistItem({
                watchlistItem: {
                    type: expressionMode ? Watchlist_WatchlistItem_Type.EXPRESSION : Watchlist_WatchlistItem_Type.SECURITY,
                    security: !expressionMode ? {
                        exchange: exchangeAndCode[0],
                        code: exchangeAndCode[1]
                    } : undefined,
                    securityExpression: expressionMode ? savedExpression : undefined
                },
                selected: !addedToWatchlist,
                watchlistId: watchlist.id,
                showHighlighted: false
            }))
            onUpdateWatchlistInState(watchlist.id, !addedToWatchlist)
        } else {
            setOpenExpressionNameDialog(true)
        }
    }, [expressionMode, seriesGroup.securityList, addedToWatchlist, rightSideWidget, selectedWatchlistId, watchlist, savedExpressions, convertedExpression])

    return (
        <>
            <FormControlLabel
                color={'inherit'}
                control={<Checkbox size={'small'} checked={addedToWatchlist} onChange={()=>onAddOrRemovalFromWatchlist(addedToWatchlist)} />}
                label={<ListItemText>{watchlist.name}</ListItemText>}
            />
            {openExpressionNameDialog && <ExpressionNameDialog onClosed={onExpressionNameDialogClosed} />}
        </>
    )
}

const AddToWatchlist = ({ seriesGroup, onClosed }: AddToWatchlistProps) => {
    const dispatch = useDispatch()
    const userWatchlists: UserWatchlists = useSelector((state: RootState) => state.watchListStore.userWatchlists)
    const container = useSelector(selectMultiPaneFullScreen)
    const chartContext = useContext(ChartContext)
    const expressionMode = chartContext?.chart?.mode === 'EXPRESSION'
    const securityKeyOrExpression = expressionMode ? chartContext?.chart?.expression : seriesGroup.securityList[0]
    const [securityAddedToWatchlists, setSecurityAddedToWatchlists] = useState<Record<string, boolean>>({})

    useEffect(() => {
        if (!userWatchlists || userWatchlists.watchlists.length === 0) {
            fetchWatchlists({ dispatch: dispatch })
        }
    }, [userWatchlists])

    const loadIsSecurityAddedToWatchlists = useCallback(async (userWatchlists: UserWatchlists, securityKeyOrExpression: string) => {
        if (!userWatchlists?.watchlists?.length || !securityKeyOrExpression) return {}
        try {
            const body = ContainsWatchlistItemRequest.create({
                watchlistIds: userWatchlists.watchlists.map((w) => w.id),
                securityOrExpression: securityKeyOrExpression
            })
            const result = await isSecurityAddedToWatchlists(body)
            setSecurityAddedToWatchlists(result.records)
        } catch (error) {
            console.error('Failed to load watchlist info:', error)
            setSecurityAddedToWatchlists({})
        }
    }, [])

    const handleUpdateWatchlistInState = useCallback((watchlistId: string, isAdded: boolean) => {
        setSecurityAddedToWatchlists((prev) => ({
            ...prev,
            [watchlistId]: isAdded,
        }))
    }, [])

    const handleAddToWatchlistClicked = useCallback(() => {
        if (userWatchlists?.watchlists?.length > 0 && securityKeyOrExpression) {
            loadIsSecurityAddedToWatchlists(userWatchlists, securityKeyOrExpression)
        }
    }, [userWatchlists?.watchlists?.length, securityKeyOrExpression, loadIsSecurityAddedToWatchlists])

    const handleClose = useCallback(() => {
        onClosed()
    }, [onClosed])

    return (
        <PopupState variant="popover" popupId="inner" parentPopupState={undefined} disableAutoFocus={true}>
            {(state) => (
                <>
                    <Box
                        component="div"
                        {...bindTrigger(state)}
                        sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
                        onClick={(e) => {
                            handleAddToWatchlistClicked()
                            state.open(e)
                        }}
                    >
                        <ListItemIcon>
                            <AddToWatchlistIcon sx={{ fontSize: '1.5rem !important' }} />
                        </ListItemIcon>
                        <ListItemText>Add to watchlist</ListItemText>
                        <ListItemIcon>
                            <ThinArrowRightIcon />
                        </ListItemIcon>
                    </Box>

                    <Menu
                        {...bindMenu(state)}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right'
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left'
                        }}
                        onClose={handleClose}
                        sx={{
                            p: 0,
                            m: 0
                        }}
                        container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}
                    >
                        {userWatchlists.watchlists
                            .filter((watchlist) => !(watchlist.shareType === Watchlist_ShareType.COMMUNITY && watchlist?.type === Watchlist_Type.REFERENCE))
                            .map((watchlist, index) => (
                                <MenuItem dense key={watchlist.id}>
                                    <WatchlistMenuItem
                                        seriesGroup={seriesGroup}
                                        watchlist={watchlist}
                                        addedToWatchlist={!!securityAddedToWatchlists[watchlist.id]}
                                        onUpdateWatchlistInState={handleUpdateWatchlistInState}
                                    />
                                </MenuItem>
                            ))}
                    </Menu>
                </>
            )}
        </PopupState>
    )
}

export default memo(AddToWatchlist)
